import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, UntypedFormControl } from '@angular/forms';
import { DEFAULT_AUTOCOMPLETE_SIZE, mapToOptions } from '@autocomplete';
import {
  AutoCompleteOptionItem,
  AutoCompleteTransformFn,
  AutocompleteWithControlsConfig,
} from '@autocomplete-with-controls';
import { ContactPerson, Contragent, User } from '@models';
import { CardTypes, searchContragent, SmartFormContainer } from '@shared';
import { BehaviorSubject, distinctUntilChanged, map, Observable, Subject } from 'rxjs';
import { InvoiceApiService } from '../../../services/invoices/invoice-api.service';

@Component({
  selector: 'app-organization-autocomplete',
  templateUrl: './organization-autocomplete.component.html',
  styleUrls: ['./organization-autocomplete.component.scss'],
})
export class OrganizationAutocompleteComponent extends SmartFormContainer implements OnInit {
  @Input() user: User;
  @Input() config: AutocompleteWithControlsConfig = {
    ariaLabel: 'Підпорядкована організація',
    placeholder: 'Підпорядкована організація',
    arrow: true,
  };
  @Input() controlName: string = 'Organization';
  @Input() size = DEFAULT_AUTOCOMPLETE_SIZE;
  private _options: Array<AutoCompleteOptionItem<Contragent>> = [];

  get options(): Array<AutoCompleteOptionItem<Contragent>> {
    return this._options;
  }

  @Input() set options(data: Array<AutoCompleteOptionItem<Contragent>>) {
    this._filteredOrgOptions.next(data);
    this._options = data;

    this.addFormControls();

    if (data.length && !this.contactSenderOrg.value) {
      this.changeOrg(data[0].option);
    }

    this.disableIfSingleOption(data);
  }

  private _filteredOrgOptions: BehaviorSubject<Array<AutoCompleteOptionItem<Contragent>>> = new BehaviorSubject<Array<AutoCompleteOptionItem<Contragent>>>([]);
  filteredOrgOptions$: Observable<Array<AutoCompleteOptionItem<Contragent>>> = this._filteredOrgOptions.asObservable();

  @Output() refUpdate: EventEmitter<Contragent> = new EventEmitter();
  @Output() controlChanged = new EventEmitter<Contragent | ''>();

  orgSearchQuery: Subject<string> = new Subject<string>();
  orgSearchQuery$: Observable<string> = this.orgSearchQuery.asObservable();

  get contactSenderOrg(): UntypedFormControl {
    return this.form?.get(this.controlName) as UntypedFormControl;
  }

  constructor(
    protected fb: FormBuilder,
    private invoiceApiService: InvoiceApiService,
  ) {
    super(fb);
  }

  addFormControls(): void {
    if (!this.form) {
      this.form = this.fb.group({
        [this.controlName]: [null],
      });
    }

    if (!this.contactSenderOrg) {
      this.form.addControl(this.controlName, this.fb.control(null));
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setUserTypeFlow();
  }

  setUserTypeFlow(): void {
    const fixedContact =
      this.user.loyaltyCardType === CardTypes.CCARD ||
      (!this.user.contacts && this.user.loyaltyCardType === CardTypes.IDENTIFICATION);

    if (this.user.contacts || fixedContact) {
      this.loadOrganization();
      this.subscribeToOrgSearch();
    } else if (this.user.contragent?.id) {
      this.changeOrg({ Ref: this.user.contragent.id } as Contragent);
    }
  }

  loadOrganization(): void {
    if (this.options.length) {
      return;
    }

    this.invoiceApiService
      .getOrganization()
      .pipe(
        map((contacts: Contragent[]) => mapToOptions(contacts)),
      )
      .subscribe((options: AutoCompleteOptionItem<Contragent>[]) => {
        if (!options.length) {
          return;
        }
        this.options = options;
        this.contactSenderOrg.setValue(this.options[0].option);
        this.changeOrg(this.options[0].option);

        this.disableIfSingleOption(this.options);
      });
  }

  private disableIfSingleOption(options: Array<AutoCompleteOptionItem<Contragent>>): void {
    if (options.length === 1) {
      this.contactSenderOrg.disable();
    }
  }

  changeOrg(organization: Contragent): void {
    this.refUpdate.emit(organization);
  }

  handleOrgSearch($event: string): void {
    this.orgSearchQuery.next($event);
  }

  private subscribeToOrgSearch(): void {
    this.orgSearchQuery$.pipe(distinctUntilChanged())
      .subscribe((query: string) => {
        const result = query.length
          ? this.options.filter((option) => searchContragent(option.option, query))
          : this.options;
        this._filteredOrgOptions.next(result);
      });
  }

  propagateChange(value: any): void {
    this.controlChanged.next(value);
  }

  orgTransformFn: AutoCompleteTransformFn<ContactPerson> = (value: string | ContactPerson) =>
    typeof value === 'string' ? value : `${(value && value.Description) || ''}`;
}
